import {
  isCloudAuthoritativeSession,
  peekErpSessionActor,
  requestErpApi
} from './erpSessionClient';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const pendingKeys = new Map();

export function bannersUseCloudDirectly(actor = peekErpSessionActor()) {
  if (typeof window === 'undefined') return false;
  if (window.electron?.runtime === 'electron') return false;
  return isCloudAuthoritativeSession(actor);
}

function normalizedId(value) {
  const id = String(value || '').trim().toLowerCase();
  if (!UUID_PATTERN.test(id)) throw new Error('Identificador cloud do banner invalido.');
  return id;
}

function normalizedVersion(value) {
  const version = String(value == null ? '' : value).trim();
  if (!/^[1-9][0-9]{0,18}$/.test(version)) {
    throw new Error('Atualize a listagem antes de alterar este banner.');
  }
  return version;
}

function assertSafePayload(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('O banner deve ser um objeto valido.');
  }
  for (const key of Object.keys(value)) {
    const normalized = String(key || '').replace(/[_-]/g, '').toLowerCase();
    if (['tenant', 'tenantid', 'empresa', 'empresaid'].includes(normalized)) {
      const error = new Error('O banner nao pode selecionar tenant ou empresa.');
      error.code = 'tenant_scope_forbidden';
      throw error;
    }
  }
  return value;
}

function randomPart() {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = stableValue(value[key]);
      return result;
    }, {});
  }
  return value;
}

async function command(path, method, body, fingerprint, suppliedKey) {
  const generated = !suppliedKey;
  const key = suppliedKey
    || pendingKeys.get(fingerprint)
    || `nivora-banner-${method.toLowerCase()}-${randomPart()}`.slice(0, 190);
  if (generated) pendingKeys.set(fingerprint, key);
  try {
    const result = await requestErpApi(path, {
      method,
      body,
      extraHeaders: { 'X-Idempotency-Key': key }
    });
    if (generated) pendingKeys.delete(fingerprint);
    return result;
  } catch (error) {
    if (generated && Number(error?.status) >= 400 && Number(error?.status) < 500) {
      pendingKeys.delete(fingerprint);
    }
    throw error;
  }
}

export function listCloudBanners() {
  return requestErpApi('/cloud/banners');
}

export function getCloudBanner(id) {
  return requestErpApi(`/cloud/banners/${encodeURIComponent(normalizedId(id))}`);
}

export function createCloudBanner(payload, idempotencyKey) {
  const safe = assertSafePayload(payload);
  const fingerprint = JSON.stringify(stableValue({ action: 'create', payload: safe }));
  return command(
    '/cloud/banners',
    'POST',
    { payload: safe },
    fingerprint,
    idempotencyKey
  );
}

export function updateCloudBanner(id, version, payload, idempotencyKey) {
  const normalized = normalizedId(id);
  const expectedVersion = normalizedVersion(version);
  const safe = assertSafePayload(payload);
  const fingerprint = JSON.stringify(stableValue({
    action: 'update',
    id: normalized,
    expectedVersion,
    payload: safe
  }));
  return command(
    `/cloud/banners/${encodeURIComponent(normalized)}`,
    'PUT',
    { expectedVersion, payload: safe },
    fingerprint,
    idempotencyKey
  );
}

export const __cloudBannersTestUtils = Object.freeze({
  resetPendingKeys() {
    pendingKeys.clear();
  }
});
